import { logger, LoggerWarningEventType } from "@/lib/logger"
import { getShouldBeMesFromEligibilityErrors } from "./getShouldBeMesFromEligibilityErrors"

// Known/expected business error codes that should not be logged as errors (reduces Datadog noise)
const ELECTRICITY_ELIGIBILITY_ERRORS_CODES = [
    "EPRM001", // Information provided by Enedis does not contain enough information about the meter or circuit breaker
    "EPRM002", // The meter is not compatible with our offer due to its power rating (power greater than 36kVA, professional meter or incorrect FTA)
    "EPRM003", // System does not support this type of meter. (eg. Tempo, EJP, professional meter)
    "EPRM004", // The current contractual status of the meter (in service / terminated / ...) or its power supply status (powered / cut off / not connected / ...) is not supported by our system
    "EPRM006", // Current tariff options for this meter aren't compatible with our offer (probably a meter with an EJP subscription)
    "EPRM015", // System does not handle points of this type (eg. Subscribed power unavailable at Enedis)
    "EPRM016", // Cannot subscribe to CDF because PRM is only eligible for MES
    "EPRM017", // Unable to subscribe because your PRM is located in a geographical area not served by our services
    "EPRM018", // System does not handle first MES
]

// Known/expected business error codes that should not be logged as errors (reduces Datadog noise)
const GAS_ELIGIBILITY_ERRORS_CODES = [
    "EPCE008", // The contractual status of this PCE is "not free"
    "EPCE017", // System does not handle first MES
]

type ExternalService = "ENEDIS" | "GRDF"

type EligibilityError = {
    code: string
    message: string
    details: string
}

type ParseEligibilityErrorsProps = {
    errors: EligibilityError[]
    meterPointId: string
    externalService: ExternalService
}

export function parseEligibilityErrors({
    errors,
    meterPointId,
    externalService,
}: ParseEligibilityErrorsProps) {
    if (!errors.length) {
        return undefined
    }

    for (const error of errors) {
        const meta = {
            code: error.code,
            meterPoint: meterPointId,
            externalService,
            feature: "onboarding",
            source: "checkMeterPointEligibility",
        }

        const errorCodes =
            externalService === "ENEDIS"
                ? ELECTRICITY_ELIGIBILITY_ERRORS_CODES
                : GAS_ELIGIBILITY_ERRORS_CODES

        const isBusinessError = errorCodes.includes(error.code)
        const logParams = {
            error: `${error.code}: ${error.message}`,
            description: error.details ?? `${error.code}: ${error.message}`,
            meta,
        }

        if (isBusinessError) {
            logger.warning({
                ...logParams,
                eventType: LoggerWarningEventType.KnownEligibilityError,
            })
        } else {
            logger.error(logParams)
        }
    }

    return {
        hasChangeOfSupplierError: getShouldBeMesFromEligibilityErrors(errors),
    }
}
